import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader, SEO } from '../../components/common';
import { searchCompaniesBySector, getSectorMetricsHistory, CompanyDetails, SectorMetricPoint } from '../../api/copilot';
import { FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';

const formatQuarterLabel = (dateStr: string) => {
  const d = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
};

const GROWTH_PERIODS = [
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
  { key: '3y', label: '3Y' },
  { key: '5y', label: '5Y' },
] as const;
type GrowthPeriod = typeof GROWTH_PERIODS[number]['key'];

const PERIOD_SUFFIX: Record<GrowthPeriod, string> = {
  quarterly: 'QoQ YoY',
  yearly: 'YoY',
  '3y': '3Y CAGR',
  '5y': '5Y CAGR',
};

const SectorDashboard: React.FC = () => {
  const { sectorName } = useParams<{ sectorName: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const decodedSector = sectorName ? decodeURIComponent(sectorName) : '';

  const [companies, setCompanies] = useState<CompanyDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<SectorMetricPoint[]>([]);
  const [growthPeriod, setGrowthPeriod] = useState<GrowthPeriod>('3y');

  useEffect(() => {
    const load = async () => {
      if (!decodedSector) return;
      try {
        setLoading(true);
        setError(null);
        const result = await searchCompaniesBySector(decodedSector, 1, 50);
        if (result.status === 'success' && result.data) {
          setCompanies(result.data);
        } else {
          setError(result.error || 'No companies found');
        }
      } catch (err) {
        console.error('Failed to load sector data:', err);
        setError('Failed to load sector data');
      } finally {
        setLoading(false);
      }
    };
    load();

    // Load historical metrics (non-blocking)
    if (decodedSector) {
      getSectorMetricsHistory(decodedSector)
        .then((data) => {
          const history = data[decodedSector] || [];
          setMetricsHistory(history);
        })
        .catch(() => {});
    }
  }, [decodedSector]);

  // Helper to read a growth metric for the selected period
  const getGM = (c: CompanyDetails, field: 'rev' | 'pat' | 'opm'): number | null => {
    return c.growth_metrics?.[growthPeriod]?.[field] ?? null;
  };

  // Compute sector aggregates
  const computeAggregates = () => {
    if (companies.length === 0) return null;

    const validPE = companies.filter(c => c.price_to_earnings && Number(c.price_to_earnings) > 0);
    const validROE = companies.filter(c => c.roe && Number(c.roe) > 0);
    const validROCE = companies.filter(c => c.roce && Number(c.roce) > 0);
    const validMcapSales = companies.filter(c => c.market_cap_to_sales && Number(c.market_cap_to_sales) > 0);
    const validPB = companies.filter(c => c.price_to_book && Number(c.price_to_book) > 0);
    const validEvEbitda = companies.filter(c => c.ev_to_ebitda && Number(c.ev_to_ebitda) > 0);

    const totalMcap = companies.reduce((sum, c) => {
      const mcap = typeof c.market_cap === 'number' ? c.market_cap : parseFloat(String(c.market_cap || '0').replace(/,/g, ''));
      return sum + (isNaN(mcap) ? 0 : mcap);
    }, 0);

    const validRevGrowth = companies.filter(c => getGM(c, 'rev') != null);
    const validPATGrowth = companies.filter(c => getGM(c, 'pat') != null);
    const validOPM = companies.filter(c => getGM(c, 'opm') != null);

    const avg = (arr: CompanyDetails[], field: keyof CompanyDetails) =>
      arr.length > 0 ? arr.reduce((s, c) => s + Number(c[field]), 0) / arr.length : 0;

    const avgGM = (arr: CompanyDetails[], field: 'rev' | 'pat' | 'opm') =>
      arr.length > 0 ? arr.reduce((s, c) => s + (getGM(c, field) ?? 0), 0) / arr.length : 0;

    return {
      count: companies.length,
      totalMcap,
      avgPE: avg(validPE, 'price_to_earnings'),
      avgROE: avg(validROE, 'roe'),
      avgROCE: avg(validROCE, 'roce'),
      avgMcapSales: avg(validMcapSales, 'market_cap_to_sales'),
      avgPB: avg(validPB, 'price_to_book'),
      avgEvEbitda: avg(validEvEbitda, 'ev_to_ebitda'),
      avgRevGrowth: avgGM(validRevGrowth, 'rev'),
      avgPATGrowth: avgGM(validPATGrowth, 'pat'),
      avgOPM: avgGM(validOPM, 'opm'),
    };
  };

  const formatMcap = (val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(0)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(0)} L`;
    return `₹${val.toLocaleString()}`;
  };

  const agg = computeAggregates();
  const hasChartData = metricsHistory.length > 1;
  const periodLabel = PERIOD_SUFFIX[growthPeriod];

  // Prepare chart data with formatted labels
  const chartData = metricsHistory.map(m => ({
    ...m,
    label: formatQuarterLabel(m.date),
  }));

  const CHART_METRICS: { key: keyof SectorMetricPoint; label: string; color: string; suffix: string; formatFn?: (v: number) => string }[] = [
    { key: 'avgPE',        label: 'P/E Ratio',        color: '#6366f1', suffix: 'x' },
    { key: 'avgPB',        label: 'P/B Ratio',        color: '#22c55e', suffix: 'x' },
    { key: 'avgMcapSales', label: 'MCap/Sales',        color: '#f59e0b', suffix: 'x' },
    { key: 'avgEvEbitda',  label: 'EV/EBITDA',         color: '#ef4444', suffix: 'x' },
    { key: 'avgROE',       label: 'ROE',               color: '#8b5cf6', suffix: '%' },
    { key: 'avgROCE',      label: 'ROCE',              color: '#14b8a6', suffix: '%' },
    { key: 'totalRevenue', label: 'Revenue',           color: '#3b82f6', suffix: ' Cr', formatFn: formatMcap },
    { key: 'totalProfit',  label: 'Net Profit',        color: '#10b981', suffix: ' Cr', formatFn: formatMcap },
    { key: 'avgOPM',       label: 'Operating Margin',  color: '#f97316', suffix: '%' },
  ];

  const [chartMetric, setChartMetric] = useState<keyof SectorMetricPoint>('avgPE');
  const activeChart = CHART_METRICS.find(m => m.key === chartMetric) || CHART_METRICS[0];

  return (
    <div className="p-4 lg:p-8 min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <SEO
        title={`${decodedSector} Sector Analysis - Yantrai`}
        description={`Explore ${decodedSector} sector companies with financial analysis, key metrics, and performance data.`}
      />
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/market')} className="p-2 rounded-full" style={{ backgroundColor: colors.inputBackground }}>
            {React.createElement(FiArrowLeft as any, { size: 24, color: colors.textColor })}
          </button>
          <div>
            <CText type="B24" color={colors.textColor}>{decodedSector}</CText>
            <CText type="r14" color={colors.grayScale500}>Sector Analysis</CText>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><CLoader /></div>
        ) : error ? (
          <div className="text-center py-8">
            <CText type="M14" color={colors.alertColor}>{error}</CText>
          </div>
        ) : (
          <>
            {/* Sector Aggregates — cards */}
            {agg && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Companies</CText>
                  <CText type="B18" color={colors.textColor}>{agg.count}</CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Total Market Cap</CText>
                  <CText type="B18" color={colors.textColor}>{formatMcap(agg.totalMcap)}</CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg P/E</CText>
                  <CText type="B18" color={agg.avgPE > 30 ? '#f59e0b' : colors.textColor}>
                    {agg.avgPE > 0 ? agg.avgPE.toFixed(1) : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg ROE</CText>
                  <CText type="B18" color={agg.avgROE > 15 ? '#22c55e' : colors.textColor}>
                    {agg.avgROE > 0 ? `${agg.avgROE.toFixed(1)}%` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg ROCE</CText>
                  <CText type="B18" color={agg.avgROCE > 15 ? '#22c55e' : colors.textColor}>
                    {agg.avgROCE > 0 ? `${agg.avgROCE.toFixed(1)}%` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg MCap/Sales</CText>
                  <CText type="B18" color={agg.avgMcapSales > 5 ? '#f59e0b' : agg.avgMcapSales > 0 && agg.avgMcapSales < 2 ? '#22c55e' : colors.textColor}>
                    {agg.avgMcapSales > 0 ? `${agg.avgMcapSales.toFixed(1)}x` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg P/B</CText>
                  <CText type="B18" color={agg.avgPB > 4 ? '#f59e0b' : agg.avgPB > 0 && agg.avgPB < 2 ? '#22c55e' : colors.textColor}>
                    {agg.avgPB > 0 ? `${agg.avgPB.toFixed(1)}x` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg EV/EBITDA</CText>
                  <CText type="B18" color={agg.avgEvEbitda > 25 ? '#f59e0b' : agg.avgEvEbitda > 0 && agg.avgEvEbitda < 15 ? '#22c55e' : colors.textColor}>
                    {agg.avgEvEbitda > 0 ? `${agg.avgEvEbitda.toFixed(1)}x` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg Rev Growth ({periodLabel})</CText>
                  <CText type="B18" color={agg.avgRevGrowth > 0 ? '#22c55e' : agg.avgRevGrowth < 0 ? '#ef4444' : colors.textColor}>
                    {agg.avgRevGrowth !== 0 ? `${agg.avgRevGrowth.toFixed(1)}%` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg PAT Growth ({periodLabel})</CText>
                  <CText type="B18" color={agg.avgPATGrowth > 0 ? '#22c55e' : agg.avgPATGrowth < 0 ? '#ef4444' : colors.textColor}>
                    {agg.avgPATGrowth !== 0 ? `${agg.avgPATGrowth.toFixed(1)}%` : '-'}
                  </CText>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}>
                  <CText type="r10" color={colors.grayScale400}>Avg OPM ({periodLabel})</CText>
                  <CText type="B18" color={agg.avgOPM > 15 ? '#22c55e' : agg.avgOPM > 0 && agg.avgOPM < 10 ? '#f59e0b' : agg.avgOPM < 0 ? '#ef4444' : colors.textColor}>
                    {agg.avgOPM !== 0 ? `${agg.avgOPM.toFixed(1)}%` : '-'}
                  </CText>
                </div>
              </div>
            )}

            {/* Historical Trends — single chart with Y-axis dropdown */}
            {hasChartData && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                    Historical Trends
                  </CText>
                  <div className="flex gap-1 flex-wrap">
                    {CHART_METRICS.map(m => (
                      <button
                        key={m.key}
                        onClick={() => setChartMetric(m.key)}
                        className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                        style={{
                          backgroundColor: chartMetric === m.key ? colors.primary : 'transparent',
                          color: chartMetric === m.key ? '#fff' : colors.grayScale500,
                          border: chartMetric === m.key ? 'none' : `1px solid ${colors.borderColor}`,
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: colors.grayScale400 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: colors.grayScale400 }}
                        width={60}
                        tickFormatter={(v: number) =>
                          activeChart.formatFn ? activeChart.formatFn(v) : `${v}${activeChart.suffix}`
                        }
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: colors.backgroundColor,
                          border: `1px solid ${colors.borderColor}`,
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(value: number) => [
                          activeChart.formatFn
                            ? activeChart.formatFn(value)
                            : `${value.toFixed(2)}${activeChart.suffix}`,
                          activeChart.label,
                        ]}
                        labelFormatter={(label) => label}
                      />
                      <Line
                        type="monotone"
                        dataKey={activeChart.key}
                        stroke={activeChart.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Growth & Profitability Comparison Bar Charts */}
            {companies.length > 0 && (() => {
              const barChartConfigs = [
                {
                  title: `Revenue Growth (${periodLabel})`,
                  data: companies
                    .filter(c => getGM(c, 'rev') != null)
                    .map(c => ({ name: c.name!.length > 18 ? c.name!.slice(0, 16) + '\u2026' : c.name!, value: getGM(c, 'rev')!, fullName: c.name! }))
                    .sort((a, b) => b.value - a.value),
                },
                {
                  title: `PAT Growth (${periodLabel})`,
                  data: companies
                    .filter(c => getGM(c, 'pat') != null)
                    .map(c => ({ name: c.name!.length > 18 ? c.name!.slice(0, 16) + '\u2026' : c.name!, value: getGM(c, 'pat')!, fullName: c.name! }))
                    .sort((a, b) => b.value - a.value),
                },
                {
                  title: `Operating Profit Margin % (${periodLabel})`,
                  data: companies
                    .filter(c => getGM(c, 'opm') != null)
                    .map(c => ({ name: c.name!.length > 18 ? c.name!.slice(0, 16) + '\u2026' : c.name!, value: getGM(c, 'opm')!, fullName: c.name! }))
                    .sort((a, b) => b.value - a.value),
                },
              ].filter(cfg => cfg.data.length > 0);

              if (barChartConfigs.length === 0) return null;

              return (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                      Growth &amp; Profitability Comparison
                    </CText>
                    <div className="flex gap-1">
                      {GROWTH_PERIODS.map(p => (
                        <button
                          key={p.key}
                          onClick={() => setGrowthPeriod(p.key)}
                          className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                          style={{
                            backgroundColor: growthPeriod === p.key ? colors.primary : 'transparent',
                            color: growthPeriod === p.key ? '#fff' : colors.grayScale500,
                          }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {barChartConfigs.map(({ title, data }) => (
                      <div
                        key={title}
                        className="p-4 rounded-xl"
                        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
                      >
                        <CText type="M12" color={colors.grayScale400} style={{ marginBottom: 8, display: 'block' }}>{title}</CText>
                        <div style={{ height: Math.max(200, data.length * 28 + 40), width: '100%' }}>
                          <ResponsiveContainer>
                            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                              <XAxis type="number" tick={{ fontSize: 10, fill: colors.grayScale400 }} />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={120}
                                tick={{ fontSize: 10, fill: colors.grayScale500 }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: colors.backgroundColor,
                                  border: `1px solid ${colors.borderColor}`,
                                  borderRadius: 8,
                                  fontSize: 12,
                                }}
                                formatter={(value: number) => [`${value.toFixed(1)}%`, title]}
                                labelFormatter={(_: string, payload: readonly any[]) => payload?.[0]?.payload?.fullName || ''}
                              />
                              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {data.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.value >= 0 ? '#22c55e' : '#ef4444'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Company List */}
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: `1px solid ${colors.borderColor}` }}
            >
              <div className="p-4" style={{ backgroundColor: colors.inputBackground }}>
                <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                  Companies in {decodedSector}
                </CText>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: colors.primary }}>
                      <th className="px-4 py-2.5 text-left text-white font-medium sticky left-0 z-10" style={{ backgroundColor: colors.primary, minWidth: 200 }}>Company</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">Price</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">Market Cap</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">P/E</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">ROE %</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">ROCE %</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">Div Yield %</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">MCap/Sales</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">P/B</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">EV/EBITDA</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">Rev Growth {periodLabel}</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">PAT Growth {periodLabel}</th>
                      <th className="px-3 py-2.5 text-right text-white font-medium whitespace-nowrap">OPM %</th>
                      <th className="px-3 py-2.5 text-center text-white font-medium w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((c, idx) => {
                      const mcap = typeof c.market_cap === 'number' ? c.market_cap : parseFloat(String(c.market_cap || '0').replace(/,/g, ''));
                      const price = typeof c.current_price === 'number' ? c.current_price : parseFloat(String(c.current_price || '0').replace(/,/g, ''));
                      const pe = c.price_to_earnings ? Number(c.price_to_earnings) : null;
                      const roe = c.roe ? Number(c.roe) : null;
                      const roce = c.roce ? Number(c.roce) : null;
                      const divYield = c.dividend_yield ? Number(c.dividend_yield) : null;
                      const mcapSales = c.market_cap_to_sales ? Number(c.market_cap_to_sales) : null;
                      const pb = c.price_to_book ? Number(c.price_to_book) : null;
                      const evEbitda = c.ev_to_ebitda ? Number(c.ev_to_ebitda) : null;
                      const revGrowth = getGM(c, 'rev');
                      const patGrowth = getGM(c, 'pat');
                      const opmPct = getGM(c, 'opm');

                      return (
                        <tr
                          key={c.name}
                          className="cursor-pointer transition-colors"
                          style={{
                            backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                            borderBottom: `1px solid ${colors.borderColor}`,
                          }}
                          onClick={() => navigate(`/company/${encodeURIComponent(c.name)}`)}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primary + '08'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground; }}
                        >
                          <td className="px-4 py-3 sticky left-0 z-10" style={{ backgroundColor: 'inherit' }}>
                            <CText type="M12" color={colors.textColor}>{c.name}</CText>
                            {c.industry && (
                              <CText type="r10" color={colors.grayScale400} style={{ display: 'block' }}>{c.industry}</CText>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="M12" color={colors.textColor}>₹{price > 0 ? price.toLocaleString() : '-'}</CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={colors.textColor}>{mcap > 0 ? formatMcap(mcap) : '-'}</CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={pe && pe > 40 ? '#f59e0b' : colors.textColor}>
                              {pe ? pe.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={roe && roe > 15 ? '#22c55e' : roe && roe < 5 ? '#ef4444' : colors.textColor}>
                              {roe ? roe.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={roce && roce > 15 ? '#22c55e' : roce && roce < 5 ? '#ef4444' : colors.textColor}>
                              {roce ? roce.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={divYield && divYield > 2 ? '#22c55e' : colors.textColor}>
                              {divYield ? divYield.toFixed(2) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={mcapSales && mcapSales > 5 ? '#f59e0b' : mcapSales && mcapSales < 2 ? '#22c55e' : colors.textColor}>
                              {mcapSales ? mcapSales.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={pb && pb > 4 ? '#f59e0b' : pb && pb < 2 ? '#22c55e' : colors.textColor}>
                              {pb ? pb.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={evEbitda && evEbitda > 25 ? '#f59e0b' : evEbitda && evEbitda < 15 ? '#22c55e' : colors.textColor}>
                              {evEbitda ? evEbitda.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={revGrowth != null && revGrowth > 0 ? '#22c55e' : revGrowth != null && revGrowth < 0 ? '#ef4444' : colors.textColor}>
                              {revGrowth != null ? `${revGrowth.toFixed(1)}%` : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={patGrowth != null && patGrowth > 0 ? '#22c55e' : patGrowth != null && patGrowth < 0 ? '#ef4444' : colors.textColor}>
                              {patGrowth != null ? `${patGrowth.toFixed(1)}%` : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <CText type="r12" color={opmPct != null && opmPct > 15 ? '#22c55e' : opmPct != null && opmPct < 10 ? '#f59e0b' : opmPct != null && opmPct < 0 ? '#ef4444' : colors.textColor}>
                              {opmPct != null ? `${opmPct.toFixed(1)}%` : '-'}
                            </CText>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {React.createElement(FiChevronRight as any, { size: 14, color: colors.grayScale400 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SectorDashboard;
